import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Container, Box, TextField, Button, Typography, Paper, 
  Alert, CircularProgress, Snackbar, Divider 
} from '@mui/material';
import GoogleSignInButton from '../components/GoogleSignInButton';
import PasswordInput from '../components/PasswordInput';
import { validatePasswordStrength } from '../utils/passwordValidation';
import PasswordRulesTooltip from '../components/PasswordRulesTooltip';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    invitationCode: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  
  const { register, googleLogin, authLoading } = useAuth();
  const navigate = useNavigate();
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));
    
    // Validate password in real-time
    if (name === 'password') {
      const { errors } = validatePasswordStrength(value);
      setPasswordErrors(errors);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.username || !formData.email || !formData.password || !formData.confirmPassword) {
      return setError('Please fill in all fields');
    }

    // Invitation code validation
    if (!formData.invitationCode || !formData.invitationCode.trim()) {
      return setError('Invitation code is required');
    }

    // Password strength validation
    const { isValid, errors } = validatePasswordStrength(formData.password);
    if (!isValid) {
      setPasswordErrors(errors);
      return setError('Password does not meet security requirements');
    }
    
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }
    
    try {
      setError('');
      const result = await register(formData.username, formData.email, formData.password, formData.invitationCode);
      
      // Handle email verification required
      if (result?.emailVerificationRequired) {
        setSuccessMessage('Registration successful! Please check your email to verify your account.');
        // Redirect to email verification pending page
        setTimeout(() => {
          navigate('/verify-email-pending', {
            state: {
              email: result.email,
              emailSent: result.emailSent,
              warning: result.warning
            }
          });
        }, 2000);
      } else {
        // Legacy flow (if email verification is not implemented yet)
        setSuccessMessage('Registration successful! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create an account');
    }
  };

  const handleGoogleSuccess = async (googleToken, invitationCode) => {
    try {
      setError('');
      await googleLogin(googleToken, invitationCode);
      setSuccessMessage('Successfully signed in with Google! Redirecting to dashboard...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      // Check if this is a "requires invitation code" error
      // Re-throw it so GoogleSignInButton can show the dialog
      if (err.response?.data?.requires_invitation_code) {
        throw err; // Re-throw to let GoogleSignInButton handle it
      }

      // For other errors, show error message on the page
      if (err.response?.status === 403) {
        setError(err.response?.data?.error || 'Invalid invitation code');
      } else {
        setError(err.response?.data?.error || 'Google sign-in failed');
      }

      // Re-throw to let GoogleSignInButton know there was an error
      throw err;
    }
  };

  const handleGoogleError = (error) => {
    setError(error || 'Google sign-in failed');
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Register
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Invitation Required</strong> - This is an invitation-only platform.
            Please enter your invitation code below to create an account.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Invitation Code"
              name="invitationCode"
              autoComplete="off"
              value={formData.invitationCode}
              onChange={handleInputChange}
              helperText="Enter the invitation code you received"
            />
            <PasswordInput
              margin="normal"
              required
              fullWidth
              name="password"
              label={<span>Password <PasswordRulesTooltip placement="right" /></span>}
              autoComplete="new-password"
              value={formData.password}
              onChange={handleInputChange}
              error={passwordErrors.length > 0}
              helperText={passwordErrors.length > 0 ? passwordErrors[0] : ''}
              showStrengthIndicator={true}
            />
            <PasswordInput
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              showStrengthIndicator={false}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={authLoading}
            >
              {authLoading ? <CircularProgress size={24} /> : 'Register'}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Divider>
            
            <GoogleSignInButton 
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={authLoading || !formData.invitationCode.trim()}
              invitationCode={formData.invitationCode}
            />
            {!formData.invitationCode.trim() && (
              <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 1 }}>
                Enter your invitation code above to sign up with Google
              </Typography>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  Already have an account? Login
                </Typography>
              </Link>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register; 